import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import TierChip from '../../../common/components/tier-chip/tier-chip';
import { formatDateTime } from '../../../core/utils/date';
import { useAuth } from '../../../global/contexts/auth/auth';
import { useRepositoryInfo, useRepositoryName, useTierLabel } from '../../../global/contexts/lookups/lookups';
import ProjectFilter from '../../projects/layouts/project-filter';
import KindBadge from '../../repositories/layouts/kind-badge';
import { useFindings } from '../contexts/findings';
import { SEVERITIES, severityOf } from '../utils/severity';

import '../styles/overview.css';

// The findings pile — the mechanism that keeps technical debt from piling up.
//
// An agent that finds something outside its task does not fix it — it files
// it and keeps going. What that buys is only real if somebody looks at the
// pile afterwards, and until this screen existed the pile was reachable only
// by filtering the task list by a state and reading `origin_task_id` by eye.
//
// READ ONLY. Promoting a finding into the queue and discarding it are the two
// halves of triage and both are published routes, but they are decisions about
// what the shop is going to spend time on. They are not part of showing the
// pile, and a button that spends somebody's week does not get added on the way
// past.

export default function FindingsOverview() {
    const [params, setParams] = useSearchParams();
    const projectId = params.get('project') || '';

    // GRAVEDAD Y ORDEN CORREN ACÁ, y no por elección: `GET /admin/tasks` toma
    // `project_id`, `workflow_state` y `kind`, y nada más — no hay parámetro de
    // prioridad ni de orden. Así que actúan sobre lo que trajo la página, que es
    // la misma muestra sobre la que esta pantalla ya avisa que está mirando.
    const severity = params.get('severity') || '';
    const sort = params.get('sort') || '';

    const { findings, origins, notFindings, loaded, total, isLoading, error } =
        useFindings({ projectId });
    const repositoryName = useRepositoryName();
    const repositoryInfo = useRepositoryInfo();
    const tierLabel = useTierLabel();
    const { can } = useAuth();
    // TSS vite/05 — can() outside JSX. The tier's screen is the edit form.
    // Read, not write: the link opens the tier's READ view now, so asking for
    // a write permission would hide it from exactly the people whose job is to
    // read the work.
    const canOpenTiers = can('tiers:read');

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        if (key === 'project') { target.delete('repo'); target.delete('tier'); }
        setParams(target);
    };

    // El filtro y el orden se aplican DESPUÉS de separar los hallazgos, sobre
    // esa lista. `byUrgency` ya la dejó peor-primero; pedir fecha la reordena
    // entera, y eso es lo que se pide cuando se quiere «lo último que apareció».
    const shown = useMemo(() => {
        let rows = findings;

        if (severity) {
            const level = SEVERITIES.find(item => item.key === severity);
            rows = level ? rows.filter(row => row.priority === level.priority) : rows;
        }

        if (sort) {
            const direction = sort === 'oldest' ? 1 : -1;
            rows = [...rows].sort((a, b) => {
                const left = new Date(a.createdAt ?? a.created_at).getTime() || 0;
                const right = new Date(b.createdAt ?? b.created_at).getTime() || 0;
                // Desempate por id: dos hallazgos del mismo segundo no pueden
                // cambiar de lugar entre dos renders.
                return left === right ? (a.id - b.id) * direction : (left - right) * direction;
            });
        }

        return rows;
    }, [findings, severity, sort]);

    if (error) {
        return (
            <p className="findings__error">
                {error.message || 'No se pudieron cargar los hallazgos'}
            </p>
        );
    }

    // El recuento se queda sobre TODOS los hallazgos de la página, no sobre los
    // filtrados: es el resumen de la pila, y un tally que cambiara con el filtro
    // dejaría de contestar «cuántos críticos hay».
    const counted = SEVERITIES.map(level => ({
        ...level,
        count: findings.filter(finding => finding.priority === level.priority).length,
    }));

    return (
        <section className="findings">
            <header className="findings__header">
                <div className="findings__heading">
                    <span className="findings__hint">Triage</span>
                    <h2 className="findings__title">Hallazgos</h2>
                </div>

            </header>

            <div className="findings__filters">
                <ProjectFilter
                    value={projectId}
                    onChange={(id) => setParam('project', id)}
                />

                <label className="findings__filter-field">
                    <span className="findings__filter-label">Gravedad</span>
                    <select
                        className="findings__filter"
                        value={severity}
                        onChange={e => setParam('severity', e.target.value)}
                    >
                        <option value="">Todas</option>
                        {SEVERITIES.map(level => (
                            <option key={level.key} value={level.key}>{level.label}</option>
                        ))}
                    </select>
                </label>

                <label className="findings__filter-field">
                    <span className="findings__filter-label">Orden</span>
                    <select
                        className="findings__filter"
                        value={sort}
                        onChange={e => setParam('sort', e.target.value)}
                    >
                        {/* El primero no es «sin orden»: es el que trae el
                            backend y que `byUrgency` deja peor-primero, y dentro
                            de un nivel el más viejo antes — un crítico del mes
                            pasado no es menos urgente que uno de esta mañana. */}
                        <option value="">Por gravedad</option>
                        <option value="newest">Fecha: más reciente</option>
                        <option value="oldest">Fecha: más antiguo</option>
                    </select>
                </label>
            </div>

            {/* Worst first, and a level with none stays on screen at zero: a
                chip that disappears when it empties makes "no hay críticos"
                indistinguishable from "nadie preguntó por los críticos". */}
            <ul className="findings__summary">
                {counted.map(level => (
                    <li key={level.key} className={`tally tally--${level.key}`}>
                        <span className="tally__count">{level.count}</span>
                        <span className="tally__label">{level.label}</span>
                    </li>
                ))}
            </ul>

            {isLoading && <p className="findings__empty">Cargando…</p>}

            {!isLoading && !shown.length && (
                <p className="findings__empty">
                    {severity && findings.length
                        ? 'Ningún hallazgo de esta página tiene esa gravedad.'
                        : 'Ningún hallazgo esperando triage. Nadie anotó nada, o ya se decidió todo.'}
                </p>
            )}

            {shown.length > 0 && (
                <ul className="findings__list">
                    {shown.map(finding => (
                        <Finding
                            key={finding.id}
                            finding={finding}
                            origin={origins.get(finding.origin_task_id)}
                            repositoryName={repositoryName}
                            repositoryInfo={repositoryInfo}
                            tier={finding.modality === 'one_off'
                                ? { short: 'Puntual', full: 'Tarea puntual: sin tier ni plan' }
                                : tierLabel(finding.tier_id)}
                            tierTo={canOpenTiers && finding.tier_id ? `/admin/tiers/${finding.tier_id}` : null}
                        />
                    ))}
                </ul>
            )}

            <footer className="findings__foot">
                <p className="findings__note">
                    Un hallazgo es una tarea que salió de otra tarea: alguien encontró algo
                    fuera de su alcance, lo anotó y siguió con lo suyo. Nace propuesta y no
                    entra a la cola hasta que alguien decida que vale el tiempo.
                </p>

                {/* WHAT `proposed` MEANS NOW, and it is not what it meant when
                    this screen was written.

                    Before the breakdown, `workflow_state=proposed` held findings
                    plus the handful of tasks a master had written and not
                    promoted. Since T02 EVERY task is born proposed, so the same
                    filter now also returns every task proposal of every tier —
                    which on a project in the middle of its breakdown is most of
                    the list. The old copy here said those rows were "written by
                    a master and not promoted yet", and that is no longer true. */}
                {notFindings > 0 && (
                    <p className="findings__note">
                        De la página leída, <strong>{notFindings}</strong> tareas propuestas no
                        salieron de otra tarea, así que no son hallazgos. Desde el desglose,
                        <strong> toda tarea nace propuesta</strong>: la mayoría de esas son
                        propuestas de algún tier esperando autorización, no deuda encontrada.{' '}
                        <Link to="/admin/development/tareas?view=list&state=proposed" className="findings__link">
                            Verlas en la lista
                        </Link>
                    </p>
                )}

                {total > loaded && (
                    <p className="findings__note findings__note--warn">
                        Se leyó <strong>una página de {loaded}</strong> propuestas sobre{' '}
                        <strong>{total}</strong>, y los hallazgos se separan de esa página acá en
                        el navegador. O sea que esta pantalla muestra los hallazgos{' '}
                        <strong>de esa muestra</strong>, no los del sistema: si el desglose está
                        en marcha, la página puede venir llena de propuestas de tiers y no traer
                        ni un hallazgo. Filtrá por proyecto para acotarla.
                    </p>
                )}

                {/* Said even when the page happens to fit, because the reason it
                    fits today is that nobody has proposed much yet. */}
                {total <= loaded && (
                    <p className="findings__note">
                        Esta pantalla separa los hallazgos del resto de las propuestas en el
                        navegador, sobre lo que trajo una página. El backend todavía no ofrece un
                        filtro que aísle los hallazgos, así que el recuento de arriba es de lo
                        leído y no un total del sistema.
                    </p>
                )}
            </footer>
        </section>
    );
}

function Finding({ finding, origin, repositoryName, repositoryInfo, tier, tierTo }) {
    const severity = severityOf(finding.priority);
    const repo = repositoryInfo(finding.repository_id);

    return (
        <li className="finding">
            <span className={`finding__severity finding__severity--${severity?.key ?? 'unknown'}`}>
                {/* A priority that is not one of the four levels is shown as the
                    number it is. Rounding it to the nearest level would hide
                    that somebody edited it by hand. */}
                {severity ? severity.label : `Prioridad ${finding.priority}`}
            </span>

            <div className="finding__body">
                <Link to={`/admin/tasks/${finding.id}`} className="finding__title">
                    {finding.title}
                </Link>

                <p className="finding__meta">
                    <span className="finding__origin">
                        Salió de{' '}
                        <Link to={`/admin/tasks/${finding.origin_task_id}`} className="finding__link">
                            #{finding.origin_task_id}
                        </Link>
                        {/* The origin's title needs its own request, so it lands
                            a moment after the row. The number is there from the
                            start and is enough to follow the trail. */}
                        {origin && <span className="finding__origin-title"> · {origin.title}</span>}
                    </span>
                    <TierChip label={tier?.short} title={tier?.full} to={tierTo} />
                    <span className="finding__repo">
                        {/* La misma insignia que el listado de repositorios:
                            forma y tono antes que palabra. */}
                        {repo && <KindBadge kind={repo.kind} label={false} />}
                        {repo?.name ?? repositoryName(finding.repository_id)}
                    </span>
                    <span className="finding__date">{formatDateTime(finding.createdAt)}</span>
                </p>

                {finding.description && (
                    <p className="finding__description">{finding.description}</p>
                )}
            </div>
        </li>
    );
}
